import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useLoading } from '../../commonHooks/useLoading';
import { useAppDispatch } from '../../Redux/Store';
import { useFeedlotId } from '../../commonHooks/useFeedlotId';
import { CommonConstants } from '../../utils/constants/CommonConstants';
import { validateAccordingType } from '../../utils/validation';
import { riderConstants } from '../../utils/constants/RiderConstants';
import {
    IRiderDetail,
    IRiderModal,
} from '../../utils/Interface/RiderInterface';
import {
    createRider,
    fetchInactiveRiderInfo,
    updateRiderDetail,
} from '../../Redux/Actions/riderActions';
import FullPageLoader from '../common/FullPageLoader';
import { useModalStatus } from '../../commonHooks/useModalStatus';
import { ActivateItemModal } from '../../apps/shippingTracker/modules/BuyPo/components/ActivateItemModal';
import { FmSettingsConstant } from '../../utils/constants/FmSettingsConstants';
import { RIDER_INITIAL_STATE } from '../../Redux/Constants/riderInitialState';

const RiderModal = ({
    showModal,
    closeModalWithChanges,
    editRiderDetails,
    showActiveField,
}: IRiderModal) => {
    const [riderDetail, setRiderDetail] =
        useState<IRiderDetail>(editRiderDetails);
    const [riderDetailNameError, setRiderDetailNameError] =
        useState<boolean>(false);
    const [inActiveRider, setInActiveRider] = useState<IRiderDetail>();
    const {
        isOpen: isActivateModalOpen,
        open: openActivateModal,
        close: closeActivateModal,
    } = useModalStatus();
    const { loading, setLoading } = useLoading();
    const dispatch = useAppDispatch();
    const feedlotId = useFeedlotId();

    const validateRiderDetails = (): boolean => {
        if (!riderDetail.name) {
            setRiderDetailNameError(true);
            return false;
        }
        return true;
    };

    const handleActivateRider = async () => {
        if (feedlotId && riderDetail.name) {
            const inactiveRiderInfo = await dispatch(
                fetchInactiveRiderInfo(feedlotId, riderDetail.name.trim()),
            );
            if (inactiveRiderInfo) {
                setInActiveRider(inactiveRiderInfo[0]);
                openActivateModal();
            }
        }
    };

    const handleUpdateRiderDetail = (): void => {
        const isValid = validateRiderDetails();
        if (feedlotId && isValid) {
            dispatch(
                updateRiderDetail(feedlotId, riderDetail, {
                    closeModalWithChanges,
                    setLoading,
                    handleActivateRider,
                }),
            );
        }
    };

    const handleCreateRider = (): void => {
        const isValid = validateRiderDetails();
        if (feedlotId && isValid) {
            dispatch(
                createRider(
                    feedlotId,
                    riderDetail,
                    closeModalWithChanges,
                    setLoading,
                    handleActivateRider,
                ),
            );
        }
    };

    const handleDetailChange = (
        fieldValue: string | number | boolean,
        fieldName: string,
        type?: string,
    ): void => {
        const isValid = validateAccordingType(type, fieldValue);
        if (isValid) {
            setRiderDetail(prevRiderDetail => ({
                ...prevRiderDetail,
                [fieldName]: fieldValue,
            }));
        }
    };

    const handleCloseActivateModal = (isActivated: boolean) => {
        if (isActivated && feedlotId) {
            if (inActiveRider) {
                const inActiveRiderCopy = {
                    ...inActiveRider,
                    active: true,
                };
                dispatch(
                    updateRiderDetail(feedlotId, inActiveRiderCopy, {
                        closeModalWithChanges,
                        setLoading,
                    }),
                );
                closeActivateModal();
            }
        } else {
            closeActivateModal();
        }
    };

    useEffect(() => {
        if (!showModal) {
            setRiderDetail({ ...RIDER_INITIAL_STATE.createRiderInitial });
        }
    }, [showModal]);

    return (
        <>
            <ActivateItemModal
                showModal={isActivateModalOpen}
                closeModalHandler={handleCloseActivateModal}
                itemName={riderDetail.name?.trim()}
                item={riderConstants.activateModalConstant}
            />
            <Modal
                className="px-2 pt-5 fm-settings-modal"
                size="lg"
                show={showModal}
                centered
                animation
                onHide={() => closeModalWithChanges(false)}
            >
                {loading && <FullPageLoader />}
                <Modal.Header closeButton className="border-bottom-0">
                    <Modal.Title className="text-dark">
                        {editRiderDetails.name
                            ? `${riderConstants.editRider} ${editRiderDetails?.name}`
                            : riderConstants.createNewRiders.heading}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-between">
                        <div className="col-5 mb-4">
                            <Form.Label className="text-secondary">
                                {riderConstants.createNewRiders.form.name.label}
                                <span className="necessaryFields">*</span>
                            </Form.Label>
                            <Form.Control
                                className={`text-secondary border-${
                                    riderDetailNameError ? 'danger' : ''
                                }`}
                                value={riderDetail?.name}
                                name={
                                    riderConstants.createNewRiders.form.name
                                        .name
                                }
                                placeholder={
                                    riderConstants.createNewRiders.form.name
                                        .placeholder
                                }
                                onChange={e =>
                                    handleDetailChange(
                                        e.target.value,
                                        e.target.name,
                                    )
                                }
                                maxLength={
                                    riderConstants.createNewRiders.form.name
                                        .maxLength
                                }
                                size="sm"
                                type="text"
                            />
                        </div>
                        {showActiveField && (
                            <div className="custom-checkbox col-5 ">
                                <input
                                    type="checkbox"
                                    checked={riderDetail.active}
                                    className="mx-2"
                                    onChange={e =>
                                        handleDetailChange(
                                            !riderDetail.active,
                                            'active',
                                        )
                                    }
                                />
                                <label className="mx-2">
                                    {FmSettingsConstant.active.label}
                                </label>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-top-0">
                    <Button
                        className="secondary"
                        onClick={() => closeModalWithChanges(false)}
                    >
                        {CommonConstants.en.cancel}
                    </Button>
                    <Button
                        onClick={
                            !editRiderDetails.name
                                ? handleCreateRider
                                : handleUpdateRiderDetail
                        }
                    >
                        {CommonConstants.en.save}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RiderModal;
